import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import type { DatesSetArg, ViewApi } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import luxonPlugin from '@fullcalendar/luxon3';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useTicketedEvents } from '@Hooks/useTicketedEvents';
import { useQueryStateFixed } from '@Utils/nuqs-fix';
import { SlidersHorizontal } from 'lucide-react';
import { parseAsNullableString, parseAsStringArray } from '@Utils/nuqs-parsers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarNavigation } from './CalendarNavigation';
import { EventDialog } from './EventDialog';
import { FilterSheet } from './FilterSheet';
import { renderEventContent } from './renderEventContent';
import type { EventStatusType, EventVisibilityType } from './schemas';
import type { CalendarEventProps, CalendarFilters } from './types';
import { VenueQuickFilter } from './VenueQuickFilter';
import type { CalendarView } from './ViewToggle';
import { ViewToggle } from './ViewToggle';
import { DateTime } from 'luxon';

const CALENDAR_TIMEZONE = 'America/New_York';

const calendarViewParser = parseAsNullableString.withDefault('dayGridMonth');

export default function EventsCalendar() {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedEventResourceType, setSelectedEventResourceType] = useState<
    'ticketed' | 'private-dining' | null
  >(null);
  const [open, setOpen] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [currentDateLabel, setCurrentDateLabel] = useState<string>('');
  const calendarRef = useRef<FullCalendar>(null);

  // Track the actual visible date range from FullCalendar
  const [visibleRange, setVisibleRange] = useState<{
    start: string;
    end: string;
  } | null>(null);

  const [venueQuery, setVenueQuery] = useQueryStateFixed<string>(
    'venue',
    parseAsNullableString,
  );
  const venueFilter = venueQuery ?? '';

  const [visibilityQuery, setVisibilityQuery] = useQueryStateFixed<string[]>(
    'visibility',
    parseAsStringArray,
  );
  const visibilityFilter = useMemo(
    () => (visibilityQuery ?? []) as EventVisibilityType[],
    [visibilityQuery],
  );

  const [statusQuery, setStatusQuery] = useQueryStateFixed<string[]>(
    'status',
    parseAsStringArray,
  );
  const statusFilter = useMemo(
    () => (statusQuery ?? []) as EventStatusType[],
    [statusQuery],
  );

  const handleVenueChange = (newVenueId: string) => {
    setVenueQuery(newVenueId || null);
  };

  // Combine all filters into a single object for FilterSheet
  const currentFilters: CalendarFilters = useMemo(
    () => ({
      venueId: venueFilter,
      visibility: visibilityFilter,
      status: statusFilter,
    }),
    [venueFilter, visibilityFilter, statusFilter],
  );

  // Check if any filters are active (for the indicator)
  const hasActiveFilters = useMemo(
    () =>
      Boolean(venueFilter) ||
      visibilityFilter.length > 0 ||
      statusFilter.length > 0,
    [venueFilter, visibilityFilter, statusFilter],
  );

  const handleApplyFilters = useCallback(
    (filters: CalendarFilters) => {
      setVenueQuery(filters.venueId || null);
      setVisibilityQuery(
        filters.visibility.length > 0 ? filters.visibility : null,
      );
      setStatusQuery(filters.status.length > 0 ? filters.status : null);
    },
    [setVenueQuery, setVisibilityQuery, setStatusQuery],
  );

  const handleClearFilters = useCallback(() => {
    setVenueQuery(null);
    setVisibilityQuery(null);
    setStatusQuery(null);
  }, [setVenueQuery, setVisibilityQuery, setStatusQuery]);

  const [calendarViewParam, setCalendarViewParam] = useQueryStateFixed<string>(
    'calendarView',
    calendarViewParser,
  );
  const resolvedCurrentView = (calendarViewParam ??
    'dayGridMonth') as CalendarView;

  const [calendarDateParam, setCalendarDateParam] = useQueryStateFixed<string>(
    'calendarDate',
    parseAsNullableString,
  );

  // Helper to format date as YYYY-MM-DD using local timezone (not UTC)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Compute the default date based on view type when no calendarDate param exists
  const defaultDateString = useMemo(() => {
    const today = new Date();

    switch (resolvedCurrentView) {
      case 'timeGridDay': {
        // Day view: default to today
        return formatLocalDate(today);
      }
      case 'timeGridWeek': {
        // Week view: default to first day of the current week (Sunday)
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return formatLocalDate(weekStart);
      }
      case 'dayGridMonth':
      default: {
        // Month view: default to first day of the current month
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return formatLocalDate(monthStart);
      }
    }
  }, [resolvedCurrentView]);

  // Helper to parse date string as local time (not UTC)
  // new Date("2026-01-01") parses as UTC midnight, which shifts to Dec 31 in EST
  const parseLocalDateString = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const resolvedCalendarDateString = calendarDateParam ?? defaultDateString;
  const resolvedCalendarDate = useMemo(() => {
    const parsed = parseLocalDateString(resolvedCalendarDateString);
    return Number.isNaN(parsed.getTime())
      ? parseLocalDateString(defaultDateString)
      : parsed;
  }, [resolvedCalendarDateString, defaultDateString]);

  // Build search params as Record<string, string> for API filtering
  // Uses the actual visible range from FullCalendar
  const searchParams = useMemo((): Record<string, string> => {
    const params: Record<string, string> = {};

    // Add venue filter if selected
    if (venueFilter) {
      params['filter[venueId]'] = venueFilter;
    }

    // Add visibility filter if selected
    if (visibilityFilter.length > 0) {
      params['filter[visibility]'] = visibilityFilter.join(',');
    }

    // Add status filter if selected
    if (statusFilter.length > 0) {
      params['filter[status]'] = statusFilter.join(',');
    }

    // Only add date filters once we have the actual visible range from FullCalendar
    if (visibleRange) {
      // Filter for events that overlap the visible calendar range:
      // - Event starts before the visible range ends
      // - Event ends after the visible range starts
      params['filter[startDateTime][lte]'] = visibleRange.end;
      params['filter[endDateTime][gte]'] = visibleRange.start;
    }

    return params;
  }, [venueFilter, visibilityFilter, statusFilter, visibleRange]);

  const { data } = useTicketedEvents({
    searchParams,
    // Only fetch once we have the visible date range from FullCalendar
    enabled: visibleRange !== null,
  });

  const events = useMemo((): CalendarEventProps[] => {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map((event) => ({
      id: event.id,
      title: event.name,
      start: event.startDateTime,
      end: event.endDateTime,
      extendedProps: {
        resourceType: 'ticketed',
        aohEvent: event,
      },
    }));
  }, [data]);

  const formatDateLabel = (view: ViewApi, viewType: CalendarView): string => {
    const start = DateTime.fromJSDate(view.activeStart).setZone(
      CALENDAR_TIMEZONE,
    );

    const end = DateTime.fromJSDate(view.activeEnd)
      .setZone(CALENDAR_TIMEZONE)
      .minus({ days: 1 });

    switch (viewType) {
      case 'dayGridMonth':
        return DateTime.fromJSDate(view.calendar.getDate())
          .setZone(CALENDAR_TIMEZONE)
          .toFormat('LLLL yyyy');

      case 'timeGridWeek':
        if (start.month === end.month) {
          return `${start.toFormat('LLL d')} – ${end.toFormat('d, yyyy')}`;
        }

        return `${start.toFormat('LLL d')} – ${end.toFormat('LLL d, yyyy')}`;

      case 'timeGridDay':
        return start.toFormat('LLLL d, yyyy');

      default:
        return start.toFormat('LLLL yyyy');
    }
  };

  const handleOpenEventDialog = (event: CalendarEventProps) => {
    setSelectedEventId(event.id);
    setSelectedEventResourceType(event.extendedProps?.resourceType || null);
    setOpen(true);
  };

  const handleViewChange = (view: CalendarView) => {
    setCalendarViewParam(view);
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(view);
      // Update label after view changes
      setTimeout(() => {
        const updatedView = calendarApi.view;
        setCurrentDateLabel(formatDateLabel(updatedView, view));
      }, 0);
    }
  };

  const toCalendarDateString = (date: Date): string =>
    DateTime.fromJSDate(date).setZone(CALENDAR_TIMEZONE).toISODate();

  const getCanonicalDateForUrl = useCallback((view: ViewApi): string => {
    const viewType = view.type as CalendarView;

    if (viewType === 'dayGridMonth') {
      return DateTime.fromJSDate(view.calendar.getDate())
        .setZone(CALENDAR_TIMEZONE)
        .startOf('month')
        .toISODate();
    }

    return toCalendarDateString(view.activeStart);
  }, []);

  const handleNavigate = (direction: 'prev' | 'next' | 'today') => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    if (direction === 'today') {
      calendarApi.today();
    } else if (direction === 'prev') {
      calendarApi.prev();
    } else if (direction === 'next') {
      calendarApi.next();
    }

    // Update URL after navigation - use setTimeout to ensure calendar has updated
    setTimeout(() => {
      const view = calendarApi.view;
      setCalendarDateParam(getCanonicalDateForUrl(view));
    }, 0);
  };

  // Sync view when the resolved view changes (in case ref wasn't ready)
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi && calendarApi.view.type !== resolvedCurrentView) {
      calendarApi.changeView(resolvedCurrentView);
    }
  }, [resolvedCurrentView]);

  // Sync calendar to the resolved date from URL params
  // Only runs when URL changes (e.g., browser back/forward, direct URL navigation)
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;

    // Use the same canonical date logic to compare current view with URL
    const currentCanonicalDate = getCanonicalDateForUrl(calendarApi.view);
    if (currentCanonicalDate === resolvedCalendarDateString) {
      return;
    }

    const targetDate = parseLocalDateString(resolvedCalendarDateString);
    if (!Number.isNaN(targetDate.getTime())) {
      calendarApi.gotoDate(targetDate);
    }
  }, [resolvedCalendarDateString, getCanonicalDateForUrl]);

  // Handle datesSet event from FullCalendar - updates label and visible range
  // This is passed as a prop to FullCalendar to ensure we catch the initial render event
  // NOTE: We do NOT update URL here to avoid circular updates - URL is updated only from handleNavigate
  const handleDatesSet = (arg: DatesSetArg) => {
    const { view } = arg;
    const viewType = view.type as CalendarView;

    setCurrentDateLabel(formatDateLabel(view, viewType));
    if (viewType !== resolvedCurrentView) {
      setCalendarViewParam(viewType);
    }

    // Update the visible range from FullCalendar's actual view bounds
    // activeStart and activeEnd represent the exact visible date range
    setVisibleRange({
      start: view.activeStart.toISOString(),
      end: view.activeEnd.toISOString(),
    });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <CalendarNavigation
          currentDateLabel={currentDateLabel}
          onNavigate={handleNavigate}
        />
        <div className="flex justify-between gap-4">
          <VenueQuickFilter
            venueFilter={venueFilter}
            handleChangeVenue={handleVenueChange}
          />
          <div className="relative">
            <Button
              variant="outline"
              icon={{
                src: <SlidersHorizontal size={16} />,
              }}
              onClick={() => setIsFilterSheetOpen(true)}
            >
              Filters
            </Button>
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="bg-primary relative inline-flex h-4 w-4 rounded-full border-2" />
              </span>
            )}
          </div>
          <ViewToggle
            currentView={resolvedCurrentView}
            onViewChange={handleViewChange}
          />
        </div>
      </div>

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, luxonPlugin]}
        initialView={resolvedCurrentView}
        initialDate={resolvedCalendarDate}
        events={events}
        timeZone="America/New_York"
        eventContent={renderEventContent}
        dayCellClassNames={() => 'bg-white'}
        dayHeaderClassNames={() => 'bg-zinc-800 text-white'}
        headerToolbar={false}
        height="auto"
        datesSet={handleDatesSet}
        eventClick={(e) => handleOpenEventDialog(e.event)}
      />

      <EventDialog
        open={open}
        onOpenChange={setOpen}
        eventId={selectedEventId}
        eventType={selectedEventResourceType}
      />

      <FilterSheet
        isOpen={isFilterSheetOpen}
        setIsOpen={setIsFilterSheetOpen}
        filters={currentFilters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
}
